import {
  AnswerType,
  ChoiceAnswer,
  MultipleChoiceQuestion,
  NumberAnswer,
  Question,
  QuestionResponse,
  QuestionType,
  QuestionTypeSelector,
  QuestionnaireElement,
  QuestionnaireResponseContent,
  QuestionnaireResponseElement,
  RatingAnswer,
  RatingQuestion,
  Section,
  SectionResponse,
  SingleChoiceQuestion,
  TextAnswer,
} from 'modules/questionnaires/lib/types'

export const isQuestionChoice = (
  question: Question
): question is MultipleChoiceQuestion | SingleChoiceQuestion => {
  return (
    question.answerType === 'multiple_choice' &&
    'maxChoices' in question &&
    'choiceOptions' in question
  )
}

export const isMultipleChoiceQuestion = (
  question: Question
): question is MultipleChoiceQuestion => {
  return isQuestionChoice(question) && question.maxChoices !== 1
}

export const isSingleChoiceQuestion = (
  question: Question
): question is MultipleChoiceQuestion | SingleChoiceQuestion => {
  return isQuestionChoice(question) && question.maxChoices === 1
}

export const isQuestionRating = (question: Question): question is RatingQuestion => {
  return question.answerType === 'rating' && 'scale' in question
}

export const getQuestionTypeSelector = (question: Question): QuestionTypeSelector => {
  if (question.answerType === 'multiple_choice') {
    return question.maxChoices === 1 ? 'single_choice' : 'multiple_choice'
  }
  return question.answerType
}

export function isChoiceAnswer(answer: AnswerType | null | undefined): answer is ChoiceAnswer {
  return answer === null || answer === undefined || 'answerChoice' in answer
}

export function isRatingAnswer(answer: AnswerType | null | undefined): answer is RatingAnswer {
  return answer === null || answer === undefined || 'answerRating' in answer
}

export function isTextAnswer(answer: AnswerType | null | undefined): answer is TextAnswer {
  return answer === null || answer === undefined || 'answerText' in answer
}

export function isNumberAnswer(answer: AnswerType | null | undefined): answer is NumberAnswer {
  return answer === null || answer === undefined || 'answerInt' in answer
}

export function isSectionResponse(item: QuestionnaireResponseElement): item is SectionResponse {
  return 'questions' in item && Array.isArray(item.questions) && 'title' in item
}

function validateResponseType(response: QuestionResponse, type: QuestionType) {
  switch (type) {
    case 'text':
      return response.question.answerType === 'text' && isTextAnswer(response.answer)
    case 'rating':
      return isQuestionRating(response.question) && isRatingAnswer(response.answer)
    case 'multiple_choice':
      return isQuestionChoice(response.question) && isChoiceAnswer(response.answer)
    case 'number':
      return response.question.answerType === 'number' && isNumberAnswer(response.answer)
  }
}

export function getResponseByType(
  responseContent: QuestionnaireResponseContent,
  type: QuestionType
) {
  return responseContent.flatMap((item) => {
    if (isSectionResponse(item)) {
      return item.questions.flatMap((question) => {
        return validateResponseType(question, type) ? question : []
      })
    }

    return validateResponseType(item, type) ? item : []
  })
}

export const isSection = (item: QuestionnaireElement): item is Section => 'questions' in item
